import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchComplaints } from "../../../Redux/Slice/Homepage";
import Loader from "../../../Components/Loader/Loader";
import SupportStyleTable from "../../../Components/Table/SupportStyleTable";
import "../../../Components/Table/DynamicTable.css";
import "../../CustomerReq/InhouseRnd.css";
import {
  ArrowLeft,
  Building2,
  PhoneCall,
  Zap,
  Hourglass,
  CheckCircle2,
  Clock,
  Eye,
  Filter,
  Flame,
  Tag,
  LayoutGrid,
  Calendar,
  Table,
  RefreshCw,
  X,
  Search,
} from "lucide-react";

// Curated avatar gradient palette (exact SaaS / support theme)
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #4f46e5, #7c3aed)", // Indigo -> Purple
  "linear-gradient(135deg, #059669, #10b981)", // Emerald -> Teal
  "linear-gradient(135deg, #2563eb, #38bdf8)", // Royal Blue -> Sky
  "linear-gradient(135deg, #d97706, #f59e0b)", // Amber -> Orange
  "linear-gradient(135deg, #0284c7, #06b6d4)", // Sky -> Cyan
  "linear-gradient(135deg, #9333ea, #c026d3)", // Violet -> Magenta
  "linear-gradient(135deg, #0d9488, #14b8a6)", // Teal -> Mint
  "linear-gradient(135deg, #ea580c, #fb923c)", // Orange -> Coral
];

const getAvatarGradient = (str) => {
  if (!str || typeof str !== "string") return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const EmployeeAvatar = ({ photo, name, size = 28 }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
  }, [photo]);

  const initials = useMemo(() => {
    if (!name || name === "-") return "EM";
    const clean = name.trim().replace(/[^a-zA-Z0-9\s]/g, "");
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase() || "EM";
  }, [name]);

  const formattedPhoto = useMemo(() => {
    if (!photo) return null;
    const p = String(photo).trim();
    if (
      p.startsWith("http://") ||
      p.startsWith("https://") ||
      p.startsWith("data:") ||
      p.startsWith("blob:")
    ) {
      return p;
    }
    const apiBase = process.env.REACT_APP_API_URL || "";
    if (p.startsWith("/")) {
      return `${apiBase}${p}`;
    }
    return `${apiBase}/${p}`;
  }, [photo]);

  if (formattedPhoto && !hasError) {
    return (
      <img
        src={formattedPhoto}
        alt={name || "Employee"}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          minWidth: `${size}px`,
          minHeight: `${size}px`,
          borderRadius: "50%",
          objectFit: "cover",
          flexShrink: 0,
          border: "1.5px solid #cbd5e1",
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
        }}
        onError={() => setHasError(true)}
      />
    );
  }

  return (
    <div
      style={{
        width: `${size}px`,
        height: `${size}px`,
        minWidth: `${size}px`,
        minHeight: `${size}px`,
        borderRadius: "50%",
        flexShrink: 0,
        background: getAvatarGradient(name),
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size <= 28 ? "10px" : "11px",
        fontWeight: "700",
        letterSpacing: "-0.5px",
        textTransform: "uppercase",
        boxShadow: "0 1px 3px rgba(0,0,0,0.14)",
        border: "1.5px solid rgba(255,255,255,0.9)",
      }}
      title={name || "User"}
    >
      {initials}
    </div>
  );
};

const AllPartyCallTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { complaintsData, complaintsLoading } = useSelector(
    (state) => state.Home
  );

  // Filters State
  const [selectedCompany, setSelectedCompany] = useState("ALL"); // "ALL" or Company Name
  const [selectedAge, setSelectedAge] = useState("ALL"); // "ALL" | "TODAY" | "WEEK" | "7TO15" | "15D+" | "30D+"
  const [selectedStatus, setSelectedStatus] = useState("ALL"); // "ALL" | "ACTIVE" | "PENDING" | "COMPLETED"
  const [partySearchTerm, setPartySearchTerm] = useState("");

  useEffect(() => {
    dispatch(fetchComplaints());
  }, [dispatch]);

  const rawComplaints = useMemo(() => {
    if (!complaintsData?.data || !Array.isArray(complaintsData.data)) return [];
    return complaintsData.data;
  }, [complaintsData]);

  // Extract accurate call creation timestamp from ObjectId or createdAt or Date
  const getCallTimestamp = (item) => {
    if (!item) return null;

    if (item.createdAt) {
      const d = new Date(item.createdAt);
      if (!isNaN(d.getTime())) return d;
    }

    const idStr = String(item._id || item.id || "");
    if (idStr.length === 24) {
      try {
        const timeFromId = parseInt(idStr.substring(0, 8), 16) * 1000;
        if (!isNaN(timeFromId) && timeFromId > 0) {
          return new Date(timeFromId);
        }
      } catch (e) {
        // ignore
      }
    }

    if (item.Date) {
      const d = new Date(item.Date);
      if (!isNaN(d.getTime())) return d;
    }

    return null;
  };

  const formatDate = (dateString, item) => {
    if (dateString) {
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) {
        return `${String(d.getDate()).padStart(2, "0")}/${String(
          d.getMonth() + 1
        ).padStart(2, "0")}/${d.getFullYear()}`;
      }
    }
    const fallback = getCallTimestamp(item);
    if (fallback) {
      return `${String(fallback.getDate()).padStart(2, "0")}/${String(
        fallback.getMonth() + 1
      ).padStart(2, "0")}/${fallback.getFullYear()}`;
    }
    return "N/A";
  };

  const formatTime = (dateString, item) => {
    if (dateString) {
      const d = new Date(dateString);
      if (!isNaN(d.getTime())) {
        const isMidnightUTC =
          d.getUTCHours() === 0 &&
          d.getUTCMinutes() === 0 &&
          d.getUTCSeconds() === 0;
        if (!isMidnightUTC) {
          let hours = d.getHours();
          const minutes = String(d.getMinutes()).padStart(2, "0");
          const ampm = hours >= 12 ? "PM" : "AM";
          hours = hours % 12 || 12;
          return `${hours}:${minutes} ${ampm}`;
        }
      }
    }

    const realTime = getCallTimestamp(item);
    if (realTime) {
      let hours = realTime.getHours();
      const minutes = String(realTime.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    }

    return "-";
  };

  const formatDuration = (diffMs) => {
    if (!diffMs || diffMs <= 0) return "-";
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHrs = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const diffMins = Math.floor((diffMs / (1000 * 60)) % 60);

    let parts = [];
    if (diffDays > 0) {
      parts.push(`${diffDays}d`);
      if (diffHrs > 0) parts.push(`${diffHrs}h`);
    } else if (diffHrs > 0) {
      parts.push(`${diffHrs}h`);
      if (diffMins > 0 || parts.length === 0) parts.push(`${diffMins}m`);
    } else {
      if (diffMins > 0) parts.push(`${diffMins}m`);
      else parts.push("< 1m");
    }

    return parts.join(" ");
  };

  // 1. Calculate how many days old the call is (Call Age)
  const getCallAgeInfo = (item, callTimestamp) => {
    const now = new Date();
    const ts = callTimestamp || (item.Date ? new Date(item.Date) : null);
    if (!ts || isNaN(ts.getTime())) {
      return { ageDays: 0, ageHours: 0, ageText: "-", ageSeverity: "new", ageMs: 0 };
    }

    const diffMs = Math.max(0, now.getTime() - ts.getTime());
    const ageDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const ageHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const ageMins = Math.floor((diffMs / (1000 * 60)) % 60);

    let ageText = "";
    let ageSeverity = "new";

    if (ageDays === 0) {
      if (ageHours === 0) {
        ageText = ageMins <= 5 ? "Just now" : `${ageMins}m ago (Today)`;
      } else {
        ageText = `Today (${ageHours}h ago)`;
      }
      ageSeverity = "new";
    } else if (ageDays === 1) {
      ageText = `1 day old (Yesterday)`;
      ageSeverity = "new";
    } else if (ageDays <= 5) {
      ageText = `${ageDays} days old`;
      ageSeverity = "medium";
    } else if (ageDays <= 15) {
      ageText = `${ageDays} days old`;
      ageSeverity = "old";
    } else {
      ageText = `${ageDays} days old`;
      ageSeverity = "critical";
    }

    return {
      ageDays,
      ageHours,
      ageText,
      ageSeverity,
      ageMs: diffMs,
    };
  };

  // 2. Calculate working duration / days in progress if status is Working/Accept
  const getWorkingInfo = (item, callTimestamp) => {
    const rawStatus = String(item?.Status || "").toLowerCase().trim();
    const isWorking =
      rawStatus === "accept" ||
      rawStatus === "accepted" ||
      rawStatus === "working" ||
      rawStatus === "running";

    if (!isWorking) {
      return {
        isWorking: false,
        workingDays: 0,
        workingHours: 0,
        workingMinutes: 0,
        workingText: "-",
        workingSortValue: 0,
      };
    }

    const now = new Date();
    let start = null;

    // A. Check TimeSlabRecord for latest Accept entry
    if (Array.isArray(item.TimeSlabRecord) && item.TimeSlabRecord.length > 0) {
      const acceptSlabs = item.TimeSlabRecord.filter(
        (s) => s && String(s.type || "").toLowerCase() === "accept" && s.Date
      );
      if (acceptSlabs.length > 0) {
        const lastSlab = acceptSlabs[acceptSlabs.length - 1];
        const d = new Date(lastSlab.Date);
        if (!isNaN(d.getTime()) && d <= now) {
          start = d;
        }
      }
    }

    // B. Check Record array for accept/working entry
    if (!start && Array.isArray(item.Record) && item.Record.length > 0) {
      const acceptRec = [...item.Record]
        .reverse()
        .find(
          (r) =>
            r &&
            (String(r.DescripTione || "").toLowerCase().includes("accept") ||
              String(r.DescripTione || "").toLowerCase().includes("working")) &&
            r.Date
        );
      if (acceptRec?.Date) {
        const d = new Date(acceptRec.Date);
        if (!isNaN(d.getTime()) && d <= now) {
          start = d;
        }
      }
    }

    // C. Fallback to call creation timestamp
    if (!start) {
      start = callTimestamp || (item.Date ? new Date(item.Date) : null);
    }

    if (!start || isNaN(start.getTime())) {
      return {
        isWorking: true,
        workingDays: 0,
        workingHours: 0,
        workingMinutes: 0,
        workingText: "Working",
        workingSortValue: 1,
      };
    }

    const diffMs = Math.max(0, now.getTime() - start.getTime());
    const workingDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const workingHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
    const workingMinutes = Math.floor((diffMs / (1000 * 60)) % 60);

    let workingText = "";
    if (workingDays === 0) {
      if (workingHours === 0) {
        workingText = `${workingMinutes}m`;
      } else {
        workingText = `${workingHours}h ${workingMinutes}m`;
      }
    } else if (workingDays === 1) {
      workingText = `1d ${workingHours}h`;
    } else {
      workingText = `${workingDays}d ${workingHours}h`;
    }

    return {
      isWorking: true,
      workingDays,
      workingHours,
      workingMinutes,
      workingText,
      workingSortValue: diffMs,
    };
  };

  const calculateTerm = (startDate, endDate, status) => {
    const s = String(status || "").toLowerCase().trim();
    if (s === "completed" || s === "complete") {
      if (!startDate || !endDate) return "-";
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (isNaN(start) || isNaN(end) || end < start) return "-";
      return formatDuration(end - start);
    }
    return "-";
  };

  // Build Distinct Company List with Counts
  const companyOptions = useMemo(() => {
    const map = new Map();
    rawComplaints.forEach((item) => {
      const name =
        item.CompanyName?.WhaClientName ||
        item.CompanyName?.CompanyName ||
        "Unknown Company";
      const id = item.CompanyName?._id || item.CompanyName;
      const isPriority = item.CompanyName?.Priority?.toLowerCase() === "on";
      const type = item.CompanyName?.Type || "";

      if (!map.has(name)) {
        map.set(name, {
          name,
          id,
          count: 0,
          isPriority,
          type,
        });
      }
      map.get(name).count += 1;
    });

    const list = Array.from(map.values());
    // Sort by count descending, then name
    list.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return list;
  }, [rawComplaints]);

  // Filter company options for quick dropdown search
  const filteredCompanyOptions = useMemo(() => {
    if (!partySearchTerm.trim()) return companyOptions;
    return companyOptions.filter((c) =>
      c.name.toLowerCase().includes(partySearchTerm.trim().toLowerCase())
    );
  }, [companyOptions, partySearchTerm]);

  // Pre-processed Rows across all calls
  const allEnrichedRows = useMemo(() => {
    return rawComplaints.map((item, index) => {
      const companyObj = item.CompanyName || {};
      const companyName =
        companyObj.WhaClientName ||
        companyObj.CompanyName ||
        (typeof item.CompanyName === "string" ? item.CompanyName : "N/A");
      const companyId = companyObj._id || item.CompanyName || "";
      const isPriority = companyObj.Priority?.toLowerCase() === "on";
      const companyType = companyObj.Type || "";

      const generatorUser = item.InPutId;
      const generatorName =
        generatorUser?.Name ||
        generatorUser?.OwnerName ||
        (typeof generatorUser === "string" ? generatorUser : "") ||
        item.Employee ||
        item.ContactPerName ||
        "-";

      const generatorPhoto =
        generatorUser?.ProfilePhoto ||
        generatorUser?.Photo ||
        generatorUser?.profile ||
        "";

      const generatorRole = generatorUser?.Role || "";
      const isGenSupport = generatorRole.toLowerCase().includes("support");
      const generatorRoleType =
        generatorName === "-" || !generatorName
          ? ""
          : isGenSupport
            ? "Support Team"
            : generatorRole
              ? "Employee"
              : item.ContactPerName === generatorName
                ? "Client Caller"
                : "Generator";

      const assignedUser = item.SuportTeamId;
      const assignedName =
        assignedUser?.Name ||
        assignedUser?.OwnerName ||
        (typeof assignedUser === "string" ? assignedUser : "-");

      const assignedPhoto =
        assignedUser?.ProfilePhoto ||
        assignedUser?.Photo ||
        assignedUser?.profile ||
        "";

      const assignedRole = assignedUser?.Role || "";
      const isSupport = assignedRole.toLowerCase().includes("support");
      const assignedRoleType =
        assignedName === "-" ? "" : isSupport ? "Support Team" : "Employee";

      const displayReq =
        (item.Requirement || "").trim() ||
        (item.PresentStatus || "").trim() ||
        "-";

      const callTimestamp = getCallTimestamp(item);
      const ageInfo = getCallAgeInfo(item, callTimestamp);
      const workingInfo = getWorkingInfo(item, callTimestamp);

      const sLower = String(item.Status || "").toLowerCase().trim();
      const isCompleted = sLower === "completed" || sLower === "complete";
      const isWorking =
        sLower === "accept" ||
        sLower === "accepted" ||
        sLower === "working" ||
        sLower === "running";
      const isPending =
        sLower === "pending" ||
        sLower === "panding" ||
        (!isCompleted && !isWorking);

      return {
        id: item._id,
        rawIndex: index + 1,
        callNo: item.CallNo || "N/A",
        companyName,
        companyId,
        isPriority,
        companyType,
        rawDate: callTimestamp
          ? callTimestamp.getTime()
          : item.Date
            ? new Date(item.Date).getTime()
            : 0,
        requestDate: formatDate(item.Date, item),
        requestTime: formatTime(item.Date, item),
        ageDays: ageInfo.ageDays,
        ageHours: ageInfo.ageHours,
        ageText: ageInfo.ageText,
        ageSeverity: ageInfo.ageSeverity,
        ageMs: ageInfo.ageMs,
        isWorking,
        workingDays: workingInfo.workingDays,
        workingHours: workingInfo.workingHours,
        workingMinutes: workingInfo.workingMinutes,
        workingText: workingInfo.workingText,
        workingSortValue: workingInfo.workingSortValue,
        isCompleted,
        isPending,
        project:
          item.Project?.project_name || item.Project?.ProjectName || "N/A",
        requirement: displayReq,
        generatorName,
        generatorPhoto,
        generatorRole,
        generatorRoleType,
        assignedToName: assignedName,
        assignedPhoto,
        assignedRole,
        assignedRoleType,
        status: item.Status || "Pending",
        term: calculateTerm(
          callTimestamp || item.Date,
          item.Complate_Date,
          item.Status
        ),
      };
    });
  }, [rawComplaints]);

  // 1. Company Filter applied first to get relevant pool
  const companyFilteredPool = useMemo(() => {
    if (selectedCompany === "ALL") return allEnrichedRows;
    return allEnrichedRows.filter((r) => r.companyName === selectedCompany);
  }, [allEnrichedRows, selectedCompany]);

  // Compute live Day-wise Age counts based on company selection
  const ageCounts = useMemo(() => {
    let all = companyFilteredPool.length;
    let today = 0;
    let week = 0;
    let days7to15 = 0;
    let days15plus = 0;
    let days30plus = 0;

    for (const r of companyFilteredPool) {
      const d = r.ageDays || 0;
      if (d === 0) today++;
      if (d <= 7) week++;
      if (d >= 7 && d < 15) days7to15++;
      if (d >= 15) days15plus++;
      if (d >= 30) days30plus++;
    }

    return { all, today, week, days7to15, days15plus, days30plus };
  }, [companyFilteredPool]);

  // Compute live Status counts based on company & age selection
  const statusCounts = useMemo(() => {
    let pool = companyFilteredPool;
    if (selectedAge === "TODAY") pool = pool.filter((r) => r.ageDays === 0);
    else if (selectedAge === "WEEK") pool = pool.filter((r) => r.ageDays <= 7);
    else if (selectedAge === "7TO15")
      pool = pool.filter((r) => r.ageDays >= 7 && r.ageDays < 15);
    else if (selectedAge === "15D+") pool = pool.filter((r) => r.ageDays >= 15);
    else if (selectedAge === "30D+") pool = pool.filter((r) => r.ageDays >= 30);

    let total = pool.length;
    let active = 0;
    let pending = 0;
    let completed = 0;

    for (const r of pool) {
      if (r.isWorking) active++;
      else if (r.isCompleted) completed++;
      else pending++;
    }

    return { total, active, pending, completed };
  }, [companyFilteredPool, selectedAge]);

  // Final Filtered Rows for Table
  const tableRows = useMemo(() => {
    let filtered = companyFilteredPool;

    // Age Filter
    if (selectedAge === "TODAY") {
      filtered = filtered.filter((r) => r.ageDays === 0);
    } else if (selectedAge === "WEEK") {
      filtered = filtered.filter((r) => r.ageDays <= 7);
    } else if (selectedAge === "7TO15") {
      filtered = filtered.filter((r) => r.ageDays >= 7 && r.ageDays < 15);
    } else if (selectedAge === "15D+") {
      filtered = filtered.filter((r) => r.ageDays >= 15);
    } else if (selectedAge === "30D+") {
      filtered = filtered.filter((r) => r.ageDays >= 30);
    }

    // Status Filter
    if (selectedStatus === "ACTIVE") {
      filtered = filtered.filter((r) => r.isWorking);
    } else if (selectedStatus === "PENDING") {
      filtered = filtered.filter((r) => r.isPending && !r.isWorking);
    } else if (selectedStatus === "COMPLETED") {
      filtered = filtered.filter((r) => r.isCompleted);
    }

    // Re-index for clean Sr. No
    return filtered.map((item, index) => ({
      ...item,
      srNo: index + 1,
    }));
  }, [companyFilteredPool, selectedAge, selectedStatus]);

  const handleRowClick = (row) => {
    if (row.companyId) {
      navigate(`/partycalldetails/${row.id}/${row.companyId}`);
    } else {
      navigate(`/ShowAllTask/CustomerrequustDetail/${row.id}`);
    }
  };

  const ageFilterOptions = [
    {
      key: "ALL",
      label: "All Time",
      count: ageCounts.all,
      activeClass: "bg-[#133A69] text-white border-[#0d284a] shadow-xs",
    },
    {
      key: "TODAY",
      label: "Today",
      count: ageCounts.today,
      activeClass: "bg-emerald-600 text-white border-emerald-700 shadow-xs",
      badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    },
    {
      key: "WEEK",
      label: "< 7 Days (Week)",
      count: ageCounts.week,
      activeClass: "bg-sky-600 text-white border-sky-700 shadow-xs",
      badgeClass: "bg-sky-50 text-sky-700 border border-sky-200",
    },
    {
      key: "7TO15",
      label: "7 - 15 Days",
      count: ageCounts.days7to15,
      activeClass: "bg-indigo-600 text-white border-indigo-700 shadow-xs",
      badgeClass: "bg-indigo-50 text-indigo-700 border border-indigo-200",
    },
    {
      key: "15D+",
      label: "15 Days +",
      count: ageCounts.days15plus,
      activeClass: "bg-amber-600 text-white border-amber-700 shadow-xs",
      badgeClass: "bg-amber-50 text-amber-800 border border-amber-200 font-bold",
    },
    {
      key: "30D+",
      label: "30 Days +",
      count: ageCounts.days30plus,
      activeClass: "bg-rose-600 text-white border-rose-700 shadow-xs",
      badgeClass: "bg-rose-50 text-rose-700 border border-rose-200 font-extrabold",
    },
  ];

  const statusFilterOptions = [
    {
      key: "ALL",
      label: "Total Requests",
      count: statusCounts.total,
      icon: <PhoneCall size={13} />,
      activeClass: "bg-[#133A69] text-white border-[#0d284a] shadow-xs",
      badgeClass: "bg-slate-100 text-slate-700",
    },
    {
      key: "ACTIVE",
      label: "Active / Working",
      count: statusCounts.active,
      icon: <Zap size={13} className="text-blue-500" />,
      activeClass: "bg-blue-600 text-white border-blue-700 shadow-xs",
      badgeClass: "bg-blue-50 text-blue-700 border border-blue-200",
    },
    {
      key: "PENDING",
      label: "Pending",
      count: statusCounts.pending,
      icon: <Hourglass size={13} className="text-amber-500" />,
      activeClass: "bg-amber-600 text-white border-amber-700 shadow-xs",
      badgeClass: "bg-amber-50 text-amber-800 border border-amber-200 font-bold",
    },
    {
      key: "COMPLETED",
      label: "Completed",
      count: statusCounts.completed,
      icon: <CheckCircle2 size={13} className="text-emerald-500" />,
      activeClass: "bg-emerald-600 text-white border-emerald-700 shadow-xs",
      badgeClass: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    },
  ];

  const columns = [
    {
      headerName: "Sr. No",
      field: "srNo",
      flex: 0.5,
      sortable: true,
      sortValue: (row) => row.srNo,
    },
    {
      headerName: "Call No",
      field: "callNo",
      flex: 0.8,
      sortable: true,
      sortValue: (row) =>
        parseInt(String(row.callNo).replace(/\D/g, "") || "0", 10),
      renderCell: ({ value }) => {
        const cleanVal = String(value || "").replace(/^#/, "");
        return (
          <span
            className="badge rounded-pill px-2.5 py-1.5 fw-bold shadow-sm inline-flex items-center justify-center font-bold"
            style={{
              backgroundColor: "#EFF6FF",
              color: "#1D4ED8",
              border: "1px solid #BFDBFE",
              borderRadius: "9999px",
              fontSize: "0.82rem",
              fontWeight: "700",
              letterSpacing: "0.02em",
              boxShadow: "0 1px 2px rgba(29, 78, 216, 0.08)",
            }}
          >
            #{cleanVal || "--"}
          </span>
        );
      },
    },
    {
      headerName: "Company Name",
      field: "companyName",
      flex: 1.8,
      sortable: true,
      renderCell: ({ row }) => (
        <div className="flex flex-col gap-0.5 py-0.5 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <Building2 size={13} className="text-slate-400 shrink-0" />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedCompany(row.companyName);
              }}
              className="font-bold text-slate-900 text-xs truncate hover:text-[#133A69] hover:underline text-left cursor-pointer transition-colors"
              title={`Click to filter by ${row.companyName}`}
            >
              {row.companyName}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {row.isPriority && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-rose-700 bg-rose-50 border border-rose-200 px-1 py-0.2 rounded-full">
                <Flame size={9} className="text-rose-500" /> Priority
              </span>
            )}
            {row.companyType && row.companyType !== "-" && (
              <span className="text-[9px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 px-1 py-0.2 rounded">
                {row.companyType}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      headerName: "Request Date & Age",
      field: "requestDate",
      flex: 1.3,
      sortable: true,
      sortValue: (row) => row.rawDate,
      renderCell: ({ row }) => (
        <div className="flex flex-col gap-1 py-0.5">
          <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
            <span>{row.requestDate}</span>
            <span className="text-[10.5px] font-medium text-slate-500">
              {row.requestTime}
            </span>
          </div>
          <div className="flex items-center">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-bold border ${
                row.ageSeverity === "new"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : row.ageSeverity === "medium"
                    ? "bg-sky-50 text-sky-700 border-sky-200"
                    : row.ageSeverity === "old"
                      ? "bg-amber-50 text-amber-800 border-amber-300"
                      : "bg-rose-50 text-rose-700 border-rose-300 font-extrabold"
              }`}
              title={`Call logged on ${row.requestDate} (${row.ageDays} days ago)`}
            >
              <Clock size={11} />
              <span>{row.ageText}</span>
            </span>
          </div>
        </div>
      ),
    },
    {
      headerName: "Project",
      field: "project",
      flex: 1.0,
      sortable: true,
      renderCell: ({ value }) =>
        value && value !== "N/A" ? (
          <span className="text-[11px] font-semibold text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md border border-slate-200 truncate inline-block max-w-[130px]">
            {value}
          </span>
        ) : (
          <span className="text-slate-400">-</span>
        ),
    },
    {
      headerName: "Requirement / Issue",
      field: "requirement",
      flex: 2.1,
      sortable: true,
      renderCell: ({ value }) => (
        <div
          className="max-w-[300px] truncate text-xs text-slate-700 font-normal leading-relaxed"
          title={value}
        >
          {value}
        </div>
      ),
    },
    {
      headerName: "Task Generator",
      field: "generatorName",
      flex: 1.3,
      sortable: true,
      renderCell: ({ row }) =>
        row.generatorName && row.generatorName !== "-" ? (
          <div className="flex items-center gap-2 py-0.5 min-w-0">
            <EmployeeAvatar
              photo={row.generatorPhoto}
              name={row.generatorName}
              size={28}
            />
            <div className="flex flex-col min-w-0">
              <span
                className="font-bold text-slate-900 text-xs truncate max-w-[130px] leading-tight"
                title={`Generated by: ${row.generatorName}${
                  row.generatorRole ? ` (${row.generatorRole})` : ""
                }`}
              >
                {row.generatorName}
              </span>
              <span
                className={`text-[10px] font-semibold truncate leading-tight mt-0.5 ${
                  row.generatorRoleType === "Support Team"
                    ? "text-indigo-700"
                    : row.generatorRoleType === "Client Caller"
                      ? "text-amber-700"
                      : "text-purple-700"
                }`}
              >
                {row.generatorRoleType || "Generator"}
              </span>
            </div>
          </div>
        ) : (
          <span className="text-slate-400 text-xs font-medium">-</span>
        ),
    },
    {
      headerName: "Assigned To",
      field: "assignedToName",
      flex: 1.3,
      sortable: true,
      renderCell: ({ row }) =>
        row.assignedToName && row.assignedToName !== "-" ? (
          <div className="flex items-center gap-2 py-0.5 min-w-0">
            <EmployeeAvatar
              photo={row.assignedPhoto}
              name={row.assignedToName}
              size={28}
            />
            <div className="flex flex-col min-w-0">
              <span
                className="font-bold text-slate-900 text-xs truncate max-w-[130px] leading-tight"
                title={`${row.assignedToName}${
                  row.assignedRole ? ` (${row.assignedRole})` : ""
                }`}
              >
                {row.assignedToName}
              </span>
              <span
                className={`text-[10px] font-semibold truncate leading-tight mt-0.5 ${
                  row.assignedRoleType === "Support Team"
                    ? "text-emerald-700"
                    : "text-blue-700"
                }`}
              >
                {row.assignedRoleType}
                {row.assignedRole ? ` • ${row.assignedRole}` : ""}
              </span>
            </div>
          </div>
        ) : (
          <span className="badge rounded-pill px-2.5 py-1 text-[10.5px] font-normal bg-red-50 text-red-600 border border-red-200">
            ⏳ Not Assigned
          </span>
        ),
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1.1,
      sortable: true,
      renderCell: ({ row, value }) => {
        const rawStatus = value || row?.status || "Pending";
        const sLower = rawStatus.toLowerCase().trim();

        let label = rawStatus;
        let bg = "#FEF3C7";
        let text = "#92400E";
        let border = "#FDE68A";
        let dot = "#D97706";
        let isWorking = false;

        if (sLower === "completed" || sLower === "complete") {
          label = "Completed";
          bg = "#D1FAE5";
          text = "#065F46";
          border = "#A7F3D0";
          dot = "#10B981";
        } else if (
          sLower === "accept" ||
          sLower === "accepted" ||
          sLower === "working" ||
          sLower === "running"
        ) {
          label = "Working";
          bg = "#EFF6FF";
          text = "#1D4ED8";
          border = "#BFDBFE";
          dot = "#2563EB";
          isWorking = true;
        } else if (sLower.includes("hold")) {
          label = "On Hold";
          bg = "#F1F5F9";
          text = "#475569";
          border = "#CBD5E1";
          dot = "#64748B";
        } else if (sLower.includes("senior")) {
          label = "Checking to Senior";
          bg = "#E0E7FF";
          text = "#3730A3";
          border = "#C7D2FE";
          dot = "#6366F1";
        } else if (sLower.includes("support")) {
          label = "Checking to Support";
          bg = "#E0E7FF";
          text = "#3730A3";
          border = "#C7D2FE";
          dot = "#6366F1";
        } else if (sLower.includes("solve") || sLower.includes("dynamic")) {
          label = "Solved By Dynamic";
          bg = "#CFFAFE";
          text = "#0E7490";
          border = "#A5F3FC";
          dot = "#06B6D4";
        } else if (sLower.includes("reject")) {
          label = "Rejected";
          bg = "#FEE2E2";
          text = "#991B1B";
          border = "#FECACA";
          dot = "#EF4444";
        } else {
          label = "Pending";
        }

        return (
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold shadow-2xs transition-all"
            style={{
              backgroundColor: bg,
              color: text,
              border: `1px solid ${border}`,
            }}
          >
            {isWorking ? (
              <span className="relative flex h-2 w-2 mr-0.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600"></span>
              </span>
            ) : (
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  backgroundColor: dot,
                  display: "inline-block",
                  flexShrink: 0,
                }}
              />
            )}
            <span className={isWorking ? "font-bold tracking-tight" : ""}>
              {label}
            </span>
          </span>
        );
      },
    },
    {
      headerName: "Working / Duration",
      field: "workingDisplay",
      flex: 1.4,
      sortable: true,
      sortValue: (row) => row.workingSortValue || (row.isCompleted ? 1 : 0),
      renderCell: ({ row }) => {
        if (row.isWorking) {
          const durationStr =
            row.workingDays > 0
              ? `${row.workingDays}d ${row.workingHours}h`
              : row.workingHours > 0
                ? `${row.workingHours}h ${row.workingMinutes}m`
                : `${row.workingMinutes}m`;

          return (
            <div className="py-0.5">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold border-2 ${
                  row.workingDays >= 7
                    ? "bg-rose-50 text-rose-900 border-rose-400 shadow-2xs"
                    : row.workingDays >= 3
                      ? "bg-amber-50 text-amber-900 border-amber-400 shadow-2xs"
                      : "bg-blue-50 text-blue-900 border-blue-400 shadow-2xs"
                }`}
                title={`Working duration: ${row.workingDays} days, ${row.workingHours} hours, ${row.workingMinutes} mins`}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-700"></span>
                </span>
                <span className="font-extrabold tracking-tight">
                  ⚡ Working: {durationStr}
                </span>
              </span>
            </div>
          );
        }

        if (row.isCompleted) {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
              ✅ Solved ({row.term && row.term !== "-" ? row.term : "Done"})
            </span>
          );
        }

        if (row.isPending) {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
              ⏳ Waiting ({row.ageDays}d old)
            </span>
          );
        }

        return (
          <span className="text-xs text-slate-600 font-medium">
            {row.term && row.term !== "-" ? row.term : "-"}
          </span>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          className="action-btn action-btn-show shadow-xs inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-lg cursor-pointer"
          title="Open call details"
        >
          <Eye size={12} />
          <span>View</span>
        </button>
      ),
    },
  ];

  const getRowClassName = (row) => {
    const s = String(row.status || "").toLowerCase().trim();
    if (s.includes("complete") || s === "completed") return "row-completed-subtle";
    if (
      s.includes("accept") ||
      s === "accepted" ||
      s === "working" ||
      s === "running"
    ) {
      return "row-working-subtle";
    }
    if (s.includes("panding") || s === "pending") return "row-pending-subtle";
    return "";
  };

  if (complaintsLoading && !rawComplaints.length) {
    return <Loader />;
  }

  return (
    <div className="px-1 pt-0 pb-4 md:px-2 md:pb-6 space-y-3">
      {/* 1. Header Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 bg-white px-4 py-2.5 rounded-xl border border-slate-200/80 shadow-2xs">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/partycalldetails")}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:text-[#133A69] hover:bg-slate-50 font-bold text-xs transition-colors cursor-pointer"
            title="Back to Party Cards"
          >
            <ArrowLeft size={14} />
            <span>Parties</span>
          </button>

          <div className="w-8 h-8 rounded-lg bg-[#133A69] text-white flex items-center justify-center shadow-xs">
            <PhoneCall size={15} />
          </div>

          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h1 className="text-sm sm:text-base font-bold text-slate-900 tracking-tight">
                All Party Call Details
              </h1>
              <span className="text-[11px] font-bold text-[#133A69] bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">
                {tableRows.length} / {allEnrichedRows.length} Calls
              </span>
            </div>
            <p className="text-[11px] text-slate-500 font-medium">
              Directory across {companyOptions.length} companies with day-wise age & status filters
            </p>
          </div>
        </div>

        {/* View Switcher: Cards View | Table View (Active) | Calendar View */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => dispatch(fetchComplaints())}
            className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:text-[#133A69] hover:bg-slate-50 transition-colors cursor-pointer"
            title="Refresh All Calls"
          >
            <RefreshCw size={14} className={complaintsLoading ? "animate-spin" : ""} />
          </button>

          <div className="inline-flex shrink-0 p-0.5 rounded-lg bg-slate-100 border border-slate-200 shadow-inner">
            <button
              type="button"
              onClick={() => navigate("/partycalldetails")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs text-slate-600 hover:text-[#133A69] hover:bg-white/80 transition-all duration-150 cursor-pointer whitespace-nowrap select-none"
              title="Switch to Cards View"
            >
              <LayoutGrid size={13} />
              <span>Cards View</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/partycalldetails/all")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs bg-[#133A69] text-white shadow-xs cursor-pointer whitespace-nowrap select-none"
              title="Table View (Active)"
            >
              <Table size={13} />
              <span>Table View</span>
            </button>
            <button
              type="button"
              onClick={() => navigate("/partycalldetails?view=calendar")}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold text-xs text-slate-600 hover:text-[#133A69] hover:bg-white/80 transition-all duration-150 cursor-pointer whitespace-nowrap select-none"
              title="Switch to Calendar View"
            >
              <Calendar size={13} />
              <span>Calendar View</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Top Filter Controls: Party Filter + Day-Wise Age + Status */}
      <div className="bg-white p-3.5 rounded-xl border border-slate-200/80 shadow-2xs space-y-3">
        {/* Row 1: Company / Party Filter Dropdown */}
        <div className="flex flex-wrap items-center justify-between gap-3 pb-2.5 border-b border-slate-100">
          <div className="flex flex-wrap items-center gap-2.5 flex-1 min-w-[280px]">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-700">
              <Building2 size={14} className="text-[#133A69]" />
              <span>Filter by Party:</span>
            </div>

            <div className="relative flex-1 max-w-md">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                className="w-full bg-slate-50 hover:bg-white border border-slate-300 focus:border-[#133A69] focus:ring-2 focus:ring-[#133A69]/20 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-800 outline-none transition-all cursor-pointer"
              >
                <option value="ALL">🏢 All Parties ({companyOptions.length} companies - {allEnrichedRows.length} calls)</option>
                {companyOptions.map((co) => (
                  <option key={co.name} value={co.name}>
                    {co.isPriority ? "⭐ " : ""}{co.name} ({co.count} calls)
                  </option>
                ))}
              </select>
            </div>

            {selectedCompany !== "ALL" && (
              <button
                type="button"
                onClick={() => setSelectedCompany("ALL")}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors cursor-pointer"
                title="Reset to All Companies"
              >
                <X size={12} />
                <span>Show All Parties</span>
              </button>
            )}
          </div>

          {selectedCompany !== "ALL" && (
            <div className="text-xs font-bold text-[#133A69] bg-blue-50 border border-blue-200 px-3 py-1 rounded-lg">
              Filtered: <span className="underline">{selectedCompany}</span> ({companyFilteredPool.length} calls)
            </div>
          )}
        </div>

        {/* Row 2: Day-Wise Age Filter Tabs (Week, 15d+, 30d+, etc.) */}
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-slate-600 mr-1 flex items-center gap-1">
              <Clock size={13} className="text-[#133A69]" /> Day-wise Age:
            </span>
            {ageFilterOptions.map((opt) => {
              const isSelected = selectedAge === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setSelectedAge(opt.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg font-bold text-xs transition-all duration-150 cursor-pointer border select-none ${
                    isSelected
                      ? opt.activeClass
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50 shadow-2xs"
                  }`}
                >
                  <span>{opt.label}</span>
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.2 rounded-full ${
                      isSelected
                        ? "bg-white/20 text-white"
                        : opt.badgeClass || "bg-slate-100 text-slate-700 border border-slate-200"
                    }`}
                  >
                    {opt.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 3: Status Filter Buttons (Active, Pending, Completed, Total) */}
        <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2 border-t border-slate-100">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-slate-600 mr-1 flex items-center gap-1">
              <Filter size={12} /> Status:
            </span>
            {statusFilterOptions.map((sf) => {
              const isSelected = selectedStatus === sf.key;
              return (
                <button
                  key={sf.key}
                  type="button"
                  onClick={() => setSelectedStatus(sf.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs transition-all duration-150 cursor-pointer border select-none ${
                    isSelected
                      ? sf.activeClass
                      : "bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50 shadow-2xs"
                  }`}
                >
                  {sf.icon}
                  <span>{sf.label}</span>
                  <span
                    className={`text-[10.5px] font-bold px-2 py-0.2 rounded-full ${
                      isSelected
                        ? "bg-white/20 text-white"
                        : sf.badgeClass || "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {sf.count}
                  </span>
                </button>
              );
            })}
          </div>

          {(selectedAge !== "ALL" || selectedStatus !== "ALL" || selectedCompany !== "ALL") && (
            <button
              type="button"
              onClick={() => {
                setSelectedCompany("ALL");
                setSelectedAge("ALL");
                setSelectedStatus("ALL");
              }}
              className="text-xs font-bold text-slate-500 hover:text-rose-600 underline cursor-pointer"
            >
              Reset All Filters
            </button>
          )}
        </div>
      </div>

      {/* 3. Master Table */}
      <div className="bg-white rounded-xl border border-slate-200/80 shadow-2xs overflow-hidden">
        <SupportStyleTable
          columns={columns}
          rows={tableRows}
          tableId="all_party_call_master_table"
          fileName={`All_Party_Calls_${new Date().toISOString().split("T")[0]}`}
          itemsPerPage={50}
          onRowClick={handleRowClick}
          getRowClassName={getRowClassName}
          fullHeight={false}
          emptyComponent={
            <div className="py-12 text-center">
              <PhoneCall size={36} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-bold text-slate-700">No calls matched your filters</p>
              <p className="text-xs text-slate-400 mt-1">
                Try selecting a different party, age range, or status tab.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSelectedCompany("ALL");
                  setSelectedAge("ALL");
                  setSelectedStatus("ALL");
                }}
                className="mt-3 px-3 py-1 rounded-lg bg-[#133A69] text-white text-xs font-bold cursor-pointer"
              >
                Reset All Filters
              </button>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default AllPartyCallTable;

